import {
  AccountTree,
  Add,
  HomeRounded,
  Link as LinkIcon,
  StickyNote2,
} from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useModal } from 'components/modal';
import { ImageRenderer } from 'components/renderField/image';
import { useIsTablet } from 'hooks/useIsMobile';
import { useDraggable } from 'providers/DnDProvider';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FinsightEntity } from 'shared';
import {
  addDockItem,
  DockItem,
  getDockItems,
  moveDockItemLeft,
  moveDockItemRight,
  removeDockItem,
  useDockChangeEvent,
} from 'utils/dashboard';

import { DockItemEditor } from './components/DockItemEditor';
import DockMenu from './components/DockMenu';
import { useDockController } from './index.controller';
import { DockProps } from './index.types';

function UrlDockItem(props: {
  item: Extract<DockItem, { type: 'url' }>;
  href: string;
  target?: string;
  rel?: string;
  onContextMenu: (e: React.MouseEvent, item: DockItem) => void;
}) {
  const { item, href, target, rel, onContextMenu } = props;
  const [imageError, setImageError] = useState(false);

  return (
    <Tooltip title={item.name} placement="top">
      <IconButton
        href={href}
        target={target}
        rel={rel}
        sx={{
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '& img': {
            width: '24px !important',
            height: '24px !important',
            objectFit: 'cover',
          },
        }}
        onContextMenu={(e) => onContextMenu(e, item)}
      >
        {item.icon && !imageError ? (
          <img
            src={item.icon}
            alt={item.name}
            style={{
              width: '24px',
              height: '24px',
              objectFit: 'cover',
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          <LinkIcon />
        )}
      </IconButton>
    </Tooltip>
  );
}

export function DockView(props: DockProps) {
  const { className } = props;

  useDockChangeEvent();
  const workspaceId = useWorkspaceId();
  const isTabletOrSmaller = useIsTablet();
  const {} = useDockController(props);
  const dockItems = getDockItems(workspaceId);
  const { showModal } = useModal();
  const params = useParams();
  const currentEntityId = params.id;

  const [dropMode, setDropMode] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: DockItem;
  } | null>(null);

  const dragContext = useDraggable();

  // Helper function to check if URL is in-app
  const isInAppUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname.startsWith('/workspace/');
    } catch {
      // If it's a relative URL, check if it starts with /workspace/
      return url.startsWith('/workspace/');
    }
  };

  // Helper function to get domain from URL for favicon
  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      // If it's a relative URL, return empty string
      return '';
    }
  };

  // Helper function to get favicon URL using Google's favicon service
  const getFaviconUrl = (url: string): string => {
    return `https://www.google.com/s2/favicons?domain=${url}&sz=32`;
  };

  // Helper function to extract URL from drag event
  const getUrlFromDragEvent = (event: React.DragEvent): string | null => {
    // Check dataTransfer for URL
    const url =
      event.dataTransfer.getData('text/uri-list') ||
      event.dataTransfer.getData('text/plain');
    if (
      url &&
      (url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('/'))
    ) {
      return url;
    }
    return null;
  };

  const handleContextMenu = (event: React.MouseEvent, item: DockItem) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            item,
          }
        : null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleMoveLeft = () => {
    if (contextMenu) {
      moveDockItemLeft(contextMenu.item);
    }
    handleClose();
  };

  const handleMoveRight = () => {
    if (contextMenu) {
      moveDockItemRight(contextMenu.item);
    }
    handleClose();
  };

  const handleDelete = () => {
    if (contextMenu) {
      removeDockItem(contextMenu.item);
    }
    handleClose();
  };

  const handleEdit = () => {
    if (contextMenu) {
      showModal('Edit Dock Item', <DockItemEditor item={contextMenu.item} />);
    }
    handleClose();
  };

  // Don't render dock on smaller screens
  if (isTabletOrSmaller) {
    return null;
  }

  return (
    <>
      <Box
        className={className}
        sx={{
          // position: 'fixed',

          zIndex: 1200,
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          '.MuiSvgIcon-root': {
            fontSize: '1em',
          },
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const url = getUrlFromDragEvent(e);
          if (
            url ||
            (dragContext.dragItem && dragContext.dragItem.type === 'entity')
          ) {
            setDropMode(true);
          }
        }}
        onDragLeave={() => {
          setDropMode(false);
        }}
        onMouseEnter={() => {
          if (dragContext.dragItem && dragContext.dragItem.type === 'entity') {
            setDropMode(true);
          }
        }}
        onMouseLeave={() => {
          setDropMode(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDropMode(false);
          if (!workspaceId) return;

          // Handle URL drop
          const url = getUrlFromDragEvent(e);
          if (url) {
            const isInApp = isInAppUrl(url);
            const name = isInApp
              ? url.split('/').pop() || 'Link'
              : getDomainFromUrl(url) || 'Link';
            const icon = isInApp ? undefined : getFaviconUrl(url);

            addDockItem({
              type: 'url',
              url,
              name,
              icon,
              workspaceId,
            });
            return;
          }

          // Handle entity drop
          if (dragContext.dragItem && dragContext.dragItem.type === 'entity') {
            const entity = dragContext.dragItem.data as FinsightEntity;
            addDockItem({
              type: 'entity',
              entityId: entity.id,
              workspaceId: workspaceId,
              name: entity.name,
              icon:
                'image' in entity.extraFields
                  ? `${entity.extraFields.image ?? ''}`
                  : undefined,
            });
          }
        }}
        onMouseUp={() => {
          setDropMode(false);
          if (!workspaceId) return;
          if (dragContext.dragItem && dragContext.dragItem.type === 'entity') {
            const entity = dragContext.dragItem.data as FinsightEntity;
            addDockItem({
              type: 'entity',
              entityId: entity.id,
              workspaceId: workspaceId,
              name: entity.name,
              icon:
                'image' in entity.extraFields
                  ? `${entity.extraFields.image ?? ''}`
                  : undefined,
            });
          }
        }}
      >
        <Stack direction="row">
          <DockMenu />
          <Tooltip title="Home" placement="top">
            <IconButton
              href={`/workspace/${workspaceId}`}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <HomeRounded />
            </IconButton>
          </Tooltip>

          <Tooltip title="Projects" placement="top">
            <IconButton
              href={`/workspace/${workspaceId}/projects`}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <AccountTree />
            </IconButton>
          </Tooltip>

          <Tooltip title="Scratchpad" placement="top">
            <IconButton
              href={`/workspace/${workspaceId}/scratchpad`}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <StickyNote2 />
            </IconButton>
          </Tooltip>

          {dockItems.map((item, index) => {
            if (item.type === 'entity') {
              const isSelected = currentEntityId === item.entityId;
              return (
                <Tooltip key={index} title={item.name} placement="top">
                  <IconButton
                    href={`/workspace/${workspaceId}/entity/${item.entityId}`}
                    sx={{
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '& img': {
                        width: '24px !important',
                        height: '24px !important',
                        objectFit: 'cover',
                      },
                      ...(isSelected && {
                        borderBottom: '2px solid white',
                        borderRadius: 0,
                      }),
                    }}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                  >
                    {item.icon ? (
                      <ImageRenderer
                        field={{} as any}
                        value={item.icon}
                        viewType="list"
                      />
                    ) : (
                      <HomeRounded />
                    )}
                  </IconButton>
                </Tooltip>
              );
            }
            if (item.type === 'url') {
              const isInApp = isInAppUrl(item.url);
              const href = isInApp
                ? item.url.startsWith('/')
                  ? item.url
                  : new URL(item.url, window.location.origin).pathname
                : item.url;
              const target = isInApp ? undefined : '_blank';
              const rel = isInApp ? undefined : 'noopener noreferrer';

              return (
                <UrlDockItem
                  key={index}
                  item={item}
                  href={href}
                  target={target}
                  rel={rel}
                  onContextMenu={handleContextMenu}
                />
              );
            }
            return null;
          })}

          {dropMode && (
            <IconButton
              sx={{
                opacity: 0.5,
                color: 'white',
              }}
            >
              <Add />
            </IconButton>
          )}
        </Stack>

        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleEdit}>Edit</MenuItem>
          <MenuItem onClick={handleMoveLeft}>Move Left</MenuItem>
          <MenuItem onClick={handleMoveRight}>Move Right</MenuItem>
          <MenuItem onClick={handleDelete}>Delete</MenuItem>
        </Menu>
      </Box>
    </>
  );
}
